// Audio generation and playback against the shop API at api.nspire.studio.
//
// Two paths to hearing a record. `fetchExistingTracks` asks the API what has
// already been generated for an album, under a playlist id derived from the
// album id, and `Jukebox.prototype.playExisting` plays one of those straight
// away. `Jukebox.play` is the slow path: it POSTs a generate request, then
// polls that playlist until a track carries a playable URL. The poll waits
// 2500ms, multiplies the wait by 1.25 each round up to a 12000ms ceiling, and
// gives up after 6 minutes.
//
// `token` is the cancellation handle: every entry point bumps it, and each
// await resumes by checking that its captured token is still current before
// touching any state.
//
// A track's audio URL can arrive under any of nine different keys;
// `trackAudioUrl` tries them in order.

const API_BASE = "https://api.nspire.studio".replace(/\/$/, "");

const hasApi = () => API_BASE.length > 0;

// One id per page load, stamped onto every generate request. Memoised on the
// function object itself.
function sessionId() {
    if (!sessionId._v) {
        sessionId._v = "vc-" + Math.floor(performance.now()).toString(36) + "-" +
            (crypto.randomUUID?.().slice(0, 8) ?? Math.floor(Math.random() * 1e9).toString(36));
    }
    return sessionId._v;
}

function trackAudioUrl(track) {
    return track?.final_audio_url ||
        track?.url ||
        track?.firebase_url ||
        track?.sonauto_cdn_audio_url ||
        track?.sonauto_extended_cdn_url ||
        track?.final_treblo_audio_url ||
        track?.stream_url ||
        track?.audioUrl ||
        track?.streamUrl ||
        null;
}

function playlistTracks(payload) {
    if (Array.isArray(payload?.tracks)) return payload.tracks;
    if (Array.isArray(payload?.playlist?.tracks)) return payload.playlist.tracks;
    return [];
}

class Jukebox {
    constructor(onState) {
        this.onState = onState || (() => {});
        // `typeof Audio < "u"` is the minifier's spelling of
        // `typeof Audio !== "undefined"`; kept as written.
        this.audio = typeof Audio < "u" ? new Audio : null;
        if (this.audio) {
            this.audio.loop = true;
            this.audio.crossOrigin = "anonymous";
            this.audio.preload = "none";
            this.audio.addEventListener("playing", () => this._set("playing"));
            this.audio.addEventListener("error", () => this._set("error", "audio failed to load"));
        }
        this.state = "idle";
        this.record = null;
        this.playlistId = null;
        this.token = 0;
    }

    _set(state, detail) {
        this.state = state;
        this.onState({
            state: state,
            detail: detail,
            record: this.record
        });
    }

    stop() {
        this.token++;
        if (this.audio) {
            this.audio.pause();
            this.audio.removeAttribute("src");
            this.audio.load();
        }
        this.record = null;
        this.playlistId = null;
        this._set("idle");
    }

    async play(record) {
        if (!record) return;
        if (!hasApi()) {
            this.record = record;
            this._set("unconfigured", "Set VITE_CRATE_API_BASE_URL to the manager origin to generate audio");
            return;
        }
        const token = ++this.token;
        this.record = record;
        this._set("requesting");
        const artist = record.similarArtist || record.artist || "";
        let playlistId = null;
        try {
            const response = await fetch(`${API_BASE}/api/cratedigging/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                signal: AbortSignal.timeout(3e4),
                body: JSON.stringify({
                    albumId: record.id,
                    albumTitle: record.title,
                    albumArtist: artist,
                    referenceArtist: artist,
                    albumYear: record.year,
                    prompt: record.sonautoPrompt || "",
                    tags: [...(Array.isArray(record.sonautoTags) ? record.sonautoTags : String(record.sonautoTags || "").split(",")).map(tag => String(tag).trim()), artist].filter(Boolean),
                    subgenreId: record.subgenreId,
                    subgenreLabel: record.subgenreLabel,
                    sessionId: sessionId()
                })
            });
            const bodyText = await response.text();
            let payload = {};
            try {
                payload = bodyText ? JSON.parse(bodyText) : {};
            } catch {}
            if (!response.ok) throw new Error(payload?.error || bodyText.slice(0, 140) || `HTTP ${response.status}`);
            playlistId = payload?.playlistId;
            if (!playlistId) throw new Error(payload?.error || `no playlistId in response (HTTP ${response.status})`);
        } catch (err) {
            if (token !== this.token) return;
            this._set("error", String(err.message || err));
            return;
        }
        if (token !== this.token) return;
        this.playlistId = playlistId;
        this._set("generating");
        const startedAt = performance.now();
        const deadlineMs = 6 * 60 * 1e3;
        let waitMs = 2500;
        while (token === this.token && performance.now() - startedAt < deadlineMs) {
            await new Promise(resolve => setTimeout(resolve, waitMs));
            waitMs = Math.min(waitMs * 1.25, 12e3);
            if (token !== this.token) return;
            try {
                const response = await fetch(`${API_BASE}/api/cratedigging/playlists/${encodeURIComponent(playlistId)}`, {
                    signal: AbortSignal.timeout(2e4)
                });
                if (!response.ok) continue;
                const payload = await response.json();
                const ready = playlistTracks(payload).map(track => ({
                    track: track,
                    url: trackAudioUrl(track)
                })).find(candidate => candidate.url);
                if (!ready) continue;
                if (token !== this.token) return;
                this._set("loading");
                this.audio.src = ready.url;
                this.nowPlaying = ready.track?.title || record.title;
                await this.audio.play().catch(err => this._set("error", String(err.message || err)));
                return;
            } catch {}
        }
        if (token === this.token) this._set("error", "timed out waiting for the track");
    }
}

const playlistIdFor = albumId => "crate-" + albumId;

const trackUrl = track => trackAudioUrl(track);

async function fetchExistingTracks(albumId) {
    if (!hasApi() || albumId == null) return {
        tracks: [],
        error: null
    };
    try {
        const response = await fetch(`${API_BASE}/api/cratedigging/playlists/${encodeURIComponent(playlistIdFor(albumId))}`, {
            signal: AbortSignal.timeout(15e3)
        });
        if (!response.ok) {
            if (response.status === 404) return {
                tracks: [],
                error: null
            };
            return {
                tracks: [],
                error: `HTTP ${response.status}`
            };
        }
        const payload = await response.json();
        return {
            tracks: playlistTracks(payload),
            error: null
        };
    } catch (err) {
        return {
            tracks: [],
            error: String(err?.message || err)
        };
    }
}

// Patched onto the prototype after the class body, as in the bundle.
Jukebox.prototype.playExisting = function(record, track) {
    const url = trackAudioUrl(track);
    if (!url || !this.audio) return false;
    this.token++;
    this.record = record;
    this.playlistId = record && record.id != null ? playlistIdFor(record.id) : null;
    this._set("loading");
    this.audio.src = url;
    this.nowPlaying = track?.name || track?.title || (record && record.title) || "";
    Promise.resolve(this.audio.play()).catch(err => this._set("error", String(err.message || err)));
    return true;
};

export { Jukebox, playlistIdFor, trackUrl, fetchExistingTracks, hasApi };
